<script setup lang="ts">
import {computed} from 'vue';
import {PulseTimeAllocation} from '@/types/DataTypes.ts';
import {useTimeAllocationColors} from '@/composables/useTimeAllocationColors.ts';
import {isDark} from '@/composables/useDark.js';

const props = defineProps<{
  timeAllocation: PulseTimeAllocation | null;
  showUnallocated?: boolean;
  showLegend?: boolean;
}>();

// Default showLegend to true for backward compatibility
const showLegend = computed(() => props.showLegend);

const { getCategoriesWithColors } = useTimeAllocationColors();

// Get categories with colors from composable
const categories = computed(() => getCategoriesWithColors());

const segments = computed(() => {
  if (!props.timeAllocation) return [];

  const allSegments = categories.value
    .map(category => ({
      ...category,
      percentage: props.timeAllocation![category.key as keyof PulseTimeAllocation] || 0
    }))
    .filter(segment => segment.percentage > 0);

  // Add no data segment if enabled and present
  if (props.showUnallocated && props.timeAllocation['unallocated']) {
    allSegments.push({
      key: 'unallocated',
      label: 'No data',
      description: null,
      color: isDark() ? '#374151' : '#F3F4F6', // Dark mode: gray-700, Light mode: gray-100
      order: 999,
      active: true,
      usageCount: 0,
      percentage: props.timeAllocation['unallocated']
    });
  }

  // Sort by percentage descending, but ensure no data is always at the end
  return allSegments.sort((a, b) => {
    if (a.key === 'unallocated') return 1;
    if (b.key === 'unallocated') return -1;
    return b.percentage - a.percentage;
  });
});

const totalWidth = computed(() => {
  return segments.value.reduce((sum, segment) => sum + segment.percentage, 0);
});
</script>

<template>
  <div v-if="timeAllocation" class="pulse-time-allocation-display">
    <div v-if="showLegend" class="flex flex-wrap gap-2 mb-2">
      <div
        v-for="segment in segments"
        :key="segment.key"
        class="flex items-center gap-1 text-xs"
        v-tooltip.top="`${segment.label}: ${segment.percentage}%${segment.description ? ' - ' + segment.description : ''}`"
      >
        <div
          class="w-3 h-3 rounded"
          :style="{ 
            backgroundColor: segment.color,
            border: segment.key === 'unallocated' ? '1px solid #9CA3AF' : 'none'
          }"
        ></div>
        <span>{{ segment.label }}</span>
      </div>
    </div>
    <!-- Stacked Bar Chart -->
    <div class="flex h-4 rounded-full overflow-hidden border border-surface-300 dark:border-surface-600">
      <div
        v-for="segment in segments"
        :key="segment.key"
        :style="{
          width: `${(segment.percentage / totalWidth) * 100}%`,
          backgroundColor: segment.color,          
        }"
        :class="[
          'flex items-center justify-center text-xs font-medium',
          segment.key === 'unallocated' ? 'text-gray-500' : 'text-white'
        ]"
        v-tooltip.top="`${segment.label}: ${segment.percentage}%`"
      >
        <span v-if="segment.percentage >= 10">{{ segment.percentage }}%</span>
      </div>
    </div>

    <!-- Legend -->

  </div>
</template>
